import {AddSchema, MGLClass} from "mobx-graphlink";
import {GetValues_ForSchema} from "js-vextensions";
import {name_general} from "../../../Utils/General/SharedPatterns.js";
import {EngineConfig_Alarms} from "./@EngineConfig/@EC_Alarms.js";
import {EngineConfig_Phases} from "./@EngineConfig/@EC_Phases.js";
import {EngineConfig_Background} from "./@EngineConfig/@EC_Background.js";
import {EngineConfig_Camera} from "./@EngineConfig/@EC_Camera.js";
import {EngineConfig_DreamNarration} from "./@EngineConfig/@EC_DreamNarration.js";
import {EngineConfig_DreamQuiz} from "./@EngineConfig/@EC_DreamQuiz.js";
import {EngineConfig_DreamRecall} from "./@EngineConfig/@EC_DreamRecall.js";
import {EngineConfig_DreamTranscribe} from "./@EngineConfig/@EC_DreamTranscribe.js";
import {EngineConfig_EEG} from "./@EngineConfig/@EC_EEG.js";
import {EngineConfig_Gyro} from "./@EngineConfig/@EC_Gyro.js";
import {EngineConfig_JourneyEntry} from "./@EngineConfig/@EC_JourneyEntry.js";
import {EngineConfig_JourneyGrid} from "./@EngineConfig/@EC_JourneyGrid.js";
import {EngineConfig_JourneyTraining} from "./@EngineConfig/@EC_JourneyTraining.js";
import {EngineConfig_JourneyVisualization} from "./@EngineConfig/@EC_JourneyVisualization.js";
import {EngineConfig_Memory} from "./@EngineConfig/@EC_Memory.js";
import {EngineConfig_ModeSwitcher} from "./@EngineConfig/@EC_ModeSwitcher.js";
import {EngineConfig_StoryExplore} from "./@EngineConfig/@EC_StoryExplore.js";
import {EngineConfig_GetDefault, EngineConfig_GetDefault_Remote} from "./@EngineConfig_Presets.js";
import {GetAllTriggerSetsInEngineConfig} from "./@TriggerSet_FieldFinder.js";
import {EngineConfig_General} from "./@EngineConfig/@EC_General.js";
import {EngineConfig_MicroSnooze} from "./@EngineConfig/@EC_MicroSnooze.js";
import {EngineConfig_ConceptLink} from "./@EngineConfig/@EC_ConceptLink.js";
import {EngineConfig_RealityCheck} from "./@EngineConfig/@EC_RealityCheck.js";
import {EngineConfig_Signals} from "./@EngineConfig/@EC_Signals.js";
import {EngineConfig_VR} from "./@EngineConfig/@EC_VR.js";
import {EngineConfig_Breath} from "./@EngineConfig/@EC_Breath.js";
import {EngineConfig_Variants} from "./@EngineConfig/@EC_Variants.js";
import {EngineConfig_MemoryPalace} from "./@EngineConfig/@EC_MemoryPalace.js";
import {EngineConfig_Visualization} from "./@EngineConfig/@EC_Visualization.js";
import {EngineConfig_Media} from "./@EngineConfig/@EC_Media.js";
import {EngineConfig_Alerts} from "./@EngineConfig/@EC_Alerts.js";

@MGLClass({table: "engineConfigs"})
export class EngineConfig {
	static GetAllTriggerSets(config: EngineConfig) {
		return GetAllTriggerSetsInEngineConfig(config);
	}
	static GetAllTriggerSequences(config: EngineConfig) {
		const triggers = EngineConfig.GetAllTriggerSets(config);
		return triggers.SelectMany(a=>(a ? a.sequences : []));
	}
	static GetAllTriggerSequenceItems(config: EngineConfig) {
		const sequences = this.GetAllTriggerSequences(config);
		return sequences.SelectMany(a=>(a ? a.items : []));
	}

	constructor(initialData: Partial<EngineConfig>, remoteConfig = false) {
		this.VSet(remoteConfig ? EngineConfig_GetDefault_Remote() : EngineConfig_GetDefault());
		Object.assign(this, initialData);
	}

	id: string;
	baseConfig?: string;
	creator: string;
	createdAt: number;
	editedAt: number;
	name: string;

	variants: EngineConfig_Variants;
	general: EngineConfig_General;
	modeSwitcher: EngineConfig_ModeSwitcher;
	background: EngineConfig_Background;
	alerts: EngineConfig_Alerts;
	camera: EngineConfig_Camera;
	gyro: EngineConfig_Gyro;
	eeg: EngineConfig_EEG;
	breath: EngineConfig_Breath;
	phases: EngineConfig_Phases;
	alarms: EngineConfig_Alarms;
	microSnooze: EngineConfig_MicroSnooze;
	memory: EngineConfig_Memory;
	memoryPalace: EngineConfig_MemoryPalace;
	vr: EngineConfig_VR;
	dreamTranscribe: EngineConfig_DreamTranscribe;
	dreamNarration: EngineConfig_DreamNarration;
	dreamRecall: EngineConfig_DreamRecall;
	dreamQuiz: EngineConfig_DreamQuiz;
	conceptLink: EngineConfig_ConceptLink;
	realityCheck: EngineConfig_RealityCheck;
	signals: EngineConfig_Signals;
	storyExplore: EngineConfig_StoryExplore;
	visualization: EngineConfig_Visualization;
	media: EngineConfig_Media;
	journeyTraining: EngineConfig_JourneyTraining;
	journeyVisualization: EngineConfig_JourneyVisualization;
	journeyGrid: EngineConfig_JourneyGrid;
	journeyEntry: EngineConfig_JourneyEntry;
}
AddSchema("EngineConfig", {
	properties: {
		id: {type: "string"},
		baseConfig: {type: "string"},
		creator: {type: "string"},
		createdAt: {type: "number"},
		editedAt: {type: "number"},
		name: {type: "string", pattern: name_general},

		variants: {$ref: "EngineConfig_Variants"},
		general: {$ref: "EngineConfig_General"},
		modeSwitcher: {$ref: "EngineConfig_ModeSwitcher"},
		background: {$ref: "EngineConfig_Background"},
		alerts: {$ref: "EngineConfig_Alerts"},
		camera: {$ref: "EngineConfig_Camera"},
		gyro: {$ref: "EngineConfig_Gyro"},
		eeg: {$ref: "EngineConfig_EEG"},
		breath: {$ref: "EngineConfig_Breath"},
		phases: {$ref: "EngineConfig_Phases"},
		alarms: {$ref: "EngineConfig_Alarms"},
		microSnooze: {$ref: "EngineConfig_MicroSnooze"},
		memory: {$ref: "EngineConfig_Memory"},
		memoryPalace: {$ref: "EngineConfig_MemoryPalace"},
		vr: {$ref: "EngineConfig_VR"},
		dreamTranscribe: {$ref: "EngineConfig_DreamTranscribe"},
		dreamNarration: {$ref: "EngineConfig_DreamNarration"},
		dreamRecall: {$ref: "EngineConfig_DreamRecall"},
		dreamQuiz: {$ref: "EngineConfig_DreamQuiz"},
		conceptLink: {$ref: "EngineConfig_ConceptLink"},
		realityCheck: {$ref: "EngineConfig_RealityCheck"},
		signals: {$ref: "EngineConfig_Signals"},
		storyExplore: {$ref: "EngineConfig_StoryExplore"},
		visualization: {$ref: "EngineConfig_Visualization"},
		media: {$ref: "EngineConfig_Media"},
		journeyTraining: {$ref: "EngineConfig_JourneyTraining"},
		journeyVisualization: {$ref: "EngineConfig_JourneyVisualization"},
		journeyGrid: {$ref: "EngineConfig_JourneyGrid"},
		journeyEntry: {$ref: "EngineConfig_JourneyEntry"},
	},
});

export enum AutoEndType {
	AtTimeOfDay = 10,
	AfterDuration = 20,
}
AddSchema("AutoEndType", {oneOf: GetValues_ForSchema(AutoEndType)});